const STORE_DIRECTORY_NAME = "trebble-recording-buffer";
const INDEXED_DB_NAME = "trebble-recording-buffer";
const INDEXED_DB_VERSION = 1;
const INDEXED_DB_FILE_STORE_NAME = "recordingFiles";
const INDEXED_DB_CHUNK_STORE_NAME = "recordingChunks";

let opfsDirectoryPromise = null;
let indexedDbPromise = null;
const recordingIdToState = new Map();

const STORAGE_BACKENDS = {
    OPFS: "opfs",
    INDEXED_DB: "indexeddb",
    MEMORY: "memory"
};

const serializeError = function(error) {
    if (!error) {
        return {
            message: "Unknown recording worker error"
        };
    }

    return {
        message: error.message || error.toString(),
        name: error.name,
        stack: error.stack
    };
};

const getStoreDirectoryHandle = async function() {
    if (!self.navigator || !self.navigator.storage || !self.navigator.storage.getDirectory) {
        return null;
    }

    if (!opfsDirectoryPromise) {
        opfsDirectoryPromise = self.navigator.storage.getDirectory().then(async function(rootDirectoryHandle) {
            return await rootDirectoryHandle.getDirectoryHandle(STORE_DIRECTORY_NAME, {
                create: true
            });
        });
    }

    return await opfsDirectoryPromise;
};

const getIndexedDb = async function() {
    if (!self.indexedDB) {
        return null;
    }

    if (!indexedDbPromise) {
        indexedDbPromise = new Promise((resolve, reject) => {
            const request = self.indexedDB.open(INDEXED_DB_NAME, INDEXED_DB_VERSION);
            request.onupgradeneeded = function(event) {
                const database = event.target.result;

                if (!database.objectStoreNames.contains(INDEXED_DB_FILE_STORE_NAME)) {
                    database.createObjectStore(INDEXED_DB_FILE_STORE_NAME, {
                        keyPath: "recordingId"
                    });
                }

                if (!database.objectStoreNames.contains(INDEXED_DB_CHUNK_STORE_NAME)) {
                    const chunkStore = database.createObjectStore(INDEXED_DB_CHUNK_STORE_NAME, {
                        keyPath: "id"
                    });
                    chunkStore.createIndex("recordingId", "recordingId", {
                        unique: false
                    });
                }
            };
            request.onsuccess = function(event) {
                resolve(event.target.result);
            };
            request.onerror = function(event) {
                reject(event.target.error || new Error("Unable to open the recording persistence IndexedDB database."));
            };
        });
    }

    return await indexedDbPromise;
};

const runIndexedDbTransaction = async function(storeNames, mode, callback) {
    const database = await getIndexedDb();
    if (!database) {
        throw new Error("IndexedDB is not available.");
    }

    return await new Promise((resolve, reject) => {
        const transaction = database.transaction(storeNames, mode);
        const stores = Array.isArray(storeNames) ? storeNames.map(function(storeName) {
            return transaction.objectStore(storeName);
        }) : [transaction.objectStore(storeNames)];

        let callbackResult = null;
        transaction.oncomplete = function() {
            resolve(callbackResult);
        };
        transaction.onerror = function(event) {
            reject(event.target.error || new Error("IndexedDB transaction failed."));
        };
        transaction.onabort = function(event) {
            reject(event.target.error || new Error("IndexedDB transaction aborted."));
        };

        try {
            callbackResult = callback.apply(null, stores);
        } catch (error) {
            reject(error);
        }
    });
};

const createState = function(recordingId, fileName, mimeType) {
    return {
        recordingId: recordingId,
        fileName: fileName,
        mimeType: mimeType,
        bytesWritten: 0,
        chunkSequence: 0,
        storageBackend: STORAGE_BACKENDS.MEMORY,
        chunks: [],
        fileHandle: null,
        syncAccessHandle: null,
        fileEntryName: recordingId + ".bin",
        initPromise: null,
        workerInitError: null,
        usesIndexedDb: false
    };
};

const loadIndexedDbFileMeta = async function(recordingId) {
    return await runIndexedDbTransaction([INDEXED_DB_FILE_STORE_NAME], "readonly", function(fileStore) {
        return new Promise((resolve, reject) => {
            const request = fileStore.get(recordingId);
            request.onsuccess = function() {
                resolve(request.result || null);
            };
            request.onerror = function(event) {
                reject(event.target.error || new Error("Unable to load the indexed DB recording metadata."));
            };
        });
    });
};

const saveIndexedDbFileMeta = async function(state) {
    return await runIndexedDbTransaction([INDEXED_DB_FILE_STORE_NAME], "readwrite", function(fileStore) {
        return new Promise((resolve, reject) => {
            const request = fileStore.put({
                recordingId: state.recordingId,
                fileName: state.fileName,
                mimeType: state.mimeType,
                bytesWritten: state.bytesWritten,
                chunkSequence: state.chunkSequence,
                updatedAt: Date.now()
            });
            request.onsuccess = function() {
                resolve(true);
            };
            request.onerror = function(event) {
                reject(event.target.error || new Error("Unable to persist the indexed DB recording metadata."));
            };
        });
    });
};

const appendChunkToIndexedDb = async function(state, chunkBuffer) {
    const nextChunkSequence = state.chunkSequence + 1;

    await runIndexedDbTransaction([INDEXED_DB_CHUNK_STORE_NAME, INDEXED_DB_FILE_STORE_NAME], "readwrite", function(chunkStore, fileStore) {
        return new Promise((resolve, reject) => {
            let pendingOperations = 2;
            const completeOperation = function() {
                pendingOperations -= 1;
                if (pendingOperations === 0) {
                    resolve(true);
                }
            };
            const rejectOperation = function(error) {
                reject(error || new Error("Unable to append a chunk to the indexed DB recording buffer."));
            };

            const chunkRequest = chunkStore.put({
                id: state.recordingId + ":" + nextChunkSequence,
                recordingId: state.recordingId,
                sequence: nextChunkSequence,
                chunkBuffer: chunkBuffer,
                createdAt: Date.now()
            });
            chunkRequest.onsuccess = completeOperation;
            chunkRequest.onerror = function(event) {
                rejectOperation(event.target.error);
            };

            const fileRequest = fileStore.put({
                recordingId: state.recordingId,
                fileName: state.fileName,
                mimeType: state.mimeType,
                bytesWritten: state.bytesWritten + chunkBuffer.byteLength,
                chunkSequence: nextChunkSequence,
                updatedAt: Date.now()
            });
            fileRequest.onsuccess = completeOperation;
            fileRequest.onerror = function(event) {
                rejectOperation(event.target.error);
            };
        });
    });

    state.chunkSequence = nextChunkSequence;
    state.bytesWritten = state.bytesWritten + chunkBuffer.byteLength;
    return state.bytesWritten;
};

const getIndexedDbChunksForRecording = async function(recordingId) {
    return await runIndexedDbTransaction([INDEXED_DB_CHUNK_STORE_NAME], "readonly", function(chunkStore) {
        return new Promise((resolve, reject) => {
            const index = chunkStore.index("recordingId");
            const request = index.getAll(recordingId);
            request.onsuccess = function() {
                const sortedChunks = (request.result || []).sort(function(firstChunk, secondChunk) {
                    return (firstChunk.sequence || 0) - (secondChunk.sequence || 0);
                });
                resolve(sortedChunks);
            };
            request.onerror = function(event) {
                reject(event.target.error || new Error("Unable to read the indexed DB recording chunks."));
            };
        });
    });
};

const deleteIndexedDbRecording = async function(recordingId) {
    const storedChunks = await getIndexedDbChunksForRecording(recordingId);
    return await runIndexedDbTransaction([INDEXED_DB_CHUNK_STORE_NAME, INDEXED_DB_FILE_STORE_NAME], "readwrite", function(chunkStore, fileStore) {
        return new Promise((resolve, reject) => {
            const deleteRequests = [];
            deleteRequests.push(fileStore.delete(recordingId));
            storedChunks.forEach(function(chunkRecord) {
                deleteRequests.push(chunkStore.delete(chunkRecord.id));
            });

            if (deleteRequests.length === 0) {
                resolve(true);
                return;
            }

            let pendingDeleteCount = deleteRequests.length;
            const onDeleteCompleted = function() {
                pendingDeleteCount -= 1;
                if (pendingDeleteCount === 0) {
                    resolve(true);
                }
            };

            deleteRequests.forEach(function(deleteRequest) {
                deleteRequest.onsuccess = onDeleteCompleted;
                deleteRequest.onerror = function(event) {
                    reject(event.target.error || new Error("Unable to delete the indexed DB recording data."));
                };
            });
        });
    });
};

const ensureRecordingState = async function(recordingId, fileName, mimeType) {
    let state = recordingIdToState.get(recordingId);
    if (state) {
        if (fileName) {
            state.fileName = fileName;
        }
        if (mimeType) {
            state.mimeType = mimeType;
        }
        return await (state.initPromise || Promise.resolve(state));
    }

    state = createState(recordingId, fileName, mimeType);
    state.initPromise = (async function() {
        try {
            const storeDirectoryHandle = await getStoreDirectoryHandle();
            if (storeDirectoryHandle) {
                state.fileHandle = await storeDirectoryHandle.getFileHandle(state.fileEntryName, {
                    create: true
                });
                if (state.fileHandle && state.fileHandle.createSyncAccessHandle) {
                    state.syncAccessHandle = await state.fileHandle.createSyncAccessHandle();
                    state.storageBackend = STORAGE_BACKENDS.OPFS;
                    const existingFile = await state.fileHandle.getFile();
                    state.bytesWritten = existingFile && existingFile.size ? existingFile.size : 0;
                    return state;
                }
            }
        } catch (error) {
            state.fileHandle = null;
            state.syncAccessHandle = null;
            state.workerInitError = serializeError(error);
        }

        try {
            const existingFileMeta = await loadIndexedDbFileMeta(recordingId);
            state.storageBackend = STORAGE_BACKENDS.INDEXED_DB;
            state.usesIndexedDb = true;
            state.fileName = existingFileMeta && existingFileMeta.fileName ? existingFileMeta.fileName : state.fileName;
            state.mimeType = existingFileMeta && existingFileMeta.mimeType ? existingFileMeta.mimeType : state.mimeType;
            state.bytesWritten = existingFileMeta && existingFileMeta.bytesWritten ? existingFileMeta.bytesWritten : 0;
            state.chunkSequence = existingFileMeta && existingFileMeta.chunkSequence ? existingFileMeta.chunkSequence : 0;
        } catch (indexedDbError) {
            state.storageBackend = STORAGE_BACKENDS.MEMORY;
            state.usesIndexedDb = false;
            state.workerInitError = state.workerInitError || serializeError(indexedDbError);
        }

        return state;
    })();

    recordingIdToState.set(recordingId, state);
    return await state.initPromise;
};

const removePersistedFileIfApplicable = async function(state, options) {
    const cleanupOptions = options || {};
    if (!state) {
        return;
    }

    if (state.storageBackend === STORAGE_BACKENDS.OPFS && state.fileHandle) {
        try {
            const storeDirectoryHandle = await getStoreDirectoryHandle();
            if (storeDirectoryHandle) {
                await storeDirectoryHandle.removeEntry(state.fileEntryName);
            }
        } catch (error) {
            if (cleanupOptions.ignoreErrors === false && (!error || error.name !== "NotFoundError")) {
                throw error;
            }
            // Ignore OPFS cleanup failures.
        }
        return;
    }

    if (state.storageBackend === STORAGE_BACKENDS.INDEXED_DB || state.usesIndexedDb) {
        try {
            await deleteIndexedDbRecording(state.recordingId);
        } catch (error) {
            if (cleanupOptions.ignoreErrors === false) {
                throw error;
            }
            // Ignore indexed DB cleanup failures.
        }
    }
};

const finalizeStateIntoFile = async function(state, options) {
    const finalizeOptions = options || {};
    const shouldCloseAccessHandle = finalizeOptions.closeAccessHandle !== false;
    const mimeType = state.mimeType || "application/octet-stream";
    const fileName = state.fileName || (state.recordingId + ".bin");

    if (state.storageBackend === STORAGE_BACKENDS.OPFS && state.fileHandle) {
        if (state.syncAccessHandle) {
            if (state.syncAccessHandle.flush) {
                state.syncAccessHandle.flush();
            }
            if (shouldCloseAccessHandle && state.syncAccessHandle.close) {
                state.syncAccessHandle.close();
            }
            if (shouldCloseAccessHandle) {
                state.syncAccessHandle = null;
            }
        }

        const fileFromOpfs = await state.fileHandle.getFile();
        state.bytesWritten = fileFromOpfs && fileFromOpfs.size ? fileFromOpfs.size : state.bytesWritten;
        return new File([fileFromOpfs], fileName, {
            type: mimeType,
            lastModified: Date.now()
        });
    }

    if (state.storageBackend === STORAGE_BACKENDS.INDEXED_DB || state.usesIndexedDb) {
        const storedChunks = await getIndexedDbChunksForRecording(state.recordingId);
        const chunkBuffers = storedChunks.map(function(chunkRecord) {
            return chunkRecord.chunkBuffer;
        });
        return new File(chunkBuffers, fileName, {
            type: mimeType,
            lastModified: Date.now()
        });
    }

    return new File(state.chunks, fileName, {
        type: mimeType,
        lastModified: Date.now()
    });
};

const appendChunkToState = async function(state, chunkBuffer) {
    const chunkUint8Array = chunkBuffer instanceof Uint8Array ? chunkBuffer : new Uint8Array(chunkBuffer);

    if (state.storageBackend === STORAGE_BACKENDS.OPFS && state.syncAccessHandle) {
        const bytesWritten = state.syncAccessHandle.write(chunkUint8Array, {
            at: state.bytesWritten
        });
        if (bytesWritten !== chunkUint8Array.byteLength) {
            throw new Error("The recording buffer could not persist the full media chunk.");
        }
        state.bytesWritten = state.bytesWritten + bytesWritten;
        if (state.syncAccessHandle.flush) {
            state.syncAccessHandle.flush();
        }
        return state.bytesWritten;
    }

    if (state.storageBackend === STORAGE_BACKENDS.INDEXED_DB || state.usesIndexedDb) {
        return await appendChunkToIndexedDb(state, chunkUint8Array.buffer.slice(0));
    }

    state.chunks.push(chunkUint8Array);
    state.bytesWritten = state.bytesWritten + chunkUint8Array.byteLength;
    return state.bytesWritten;
};

const releaseStateResources = async function(state) {
    if (!state) {
        return;
    }

    if (state.syncAccessHandle && state.syncAccessHandle.close) {
        try {
            state.syncAccessHandle.close();
        } catch (_) {
            // Ignore access-handle close failures during release.
        }
        state.syncAccessHandle = null;
    }
};

self.onmessage = async function(event) {
    const data = event.data || {};
    const requestId = data.requestId;
    const type = data.type;

    const postSuccess = function(payload) {
        self.postMessage(Object.assign({
            requestId: requestId,
            type: "success"
        }, payload || {}));
    };

    const postError = function(error) {
        self.postMessage({
            requestId: requestId,
            type: "error",
            error: serializeError(error)
        });
    };

    try {
        if (type === "appendChunk") {
            const state = await ensureRecordingState(data.recordingId, data.fileName, data.mimeType);
            const totalBytesWritten = await appendChunkToState(state, data.chunkBuffer);
            postSuccess({
                totalBytesWritten: totalBytesWritten,
                storageBackend: state.storageBackend,
                workerInitError: state.workerInitError || null
            });
            return;
        }

        if (type === "reset") {
            const state = recordingIdToState.get(data.recordingId) || await ensureRecordingState(data.recordingId, data.fileName, data.mimeType);
            if (state) {
                await releaseStateResources(state);
                await removePersistedFileIfApplicable(state, {
                    ignoreErrors: false
                });
                recordingIdToState.delete(data.recordingId);
            }
            postSuccess({
                totalBytesWritten: 0,
                storageBackend: state && state.storageBackend ? state.storageBackend : null,
                workerInitError: state && state.workerInitError ? state.workerInitError : null
            });
            return;
        }

        if (type === "finalize") {
            const state = await ensureRecordingState(data.recordingId, data.fileName, data.mimeType);
            const file = await finalizeStateIntoFile(state);
            postSuccess({
                file: file,
                totalBytesWritten: state.bytesWritten,
                storageBackend: state.storageBackend,
                workerInitError: state.workerInitError || null
            });
            return;
        }

        if (type === "readPersistedFile") {
            const state = await ensureRecordingState(data.recordingId, data.fileName, data.mimeType);
            const file = await finalizeStateIntoFile(state, {
                closeAccessHandle: false
            });
            postSuccess({
                file: file,
                totalBytesWritten: state.bytesWritten,
                storageBackend: state.storageBackend,
                workerInitError: state.workerInitError || null
            });
            return;
        }

        if (type === "release") {
            const state = recordingIdToState.get(data.recordingId);
            if (state) {
                await releaseStateResources(state);
                recordingIdToState.delete(data.recordingId);
            }
            postSuccess({});
            return;
        }

        if (type === "clear") {
            const state = recordingIdToState.get(data.recordingId) || await ensureRecordingState(data.recordingId, data.fileName, data.mimeType);
            if (state) {
                await releaseStateResources(state);
                await removePersistedFileIfApplicable(state);
                recordingIdToState.delete(data.recordingId);
            }
            postSuccess({});
            return;
        }

        throw new Error("Unknown recording worker message: " + type);
    } catch (error) {
        postError(error);
    }
};
